import React, { useEffect, useMemo, useRef, useState } from 'react'
import Flatpickr from 'react-flatpickr';
import { useMutation, useQuery } from 'react-query';
import { add_appointemnt_note, change_appointment_status, delete_appointment_note, get_all_appointment_notes, get_filtered_appointments, update_appointemnt_note } from '../api/appointmentsApi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { formatDateFlatpickr, formatDynamicDateTime } from '../../../helper-functions/DateFunction';
import { KTIcon, KTSVG } from '../../../../_metronic/helpers';
import { getPeopleId } from '../../../helper-functions/general_settings_helper';
import { useGeneralSettings } from '../../../contexts/GeneralSettingsContext';
// Interface definitions for type safety
interface Appointment {
    id: number;
    location_id: string;
    location_type: number | undefined;
    subcategory_id: string | undefined;
    subcategory_title: string | undefined;
    people_id: string | undefined;
    people_name: string | undefined;
    appointment_date_time: string;
    full_name: string | undefined;
    phone_number: string | undefined;
    message: string | undefined;
    email: string | undefined;
    appointment_status: string | undefined;
    created_at: string | undefined;
    updated_by: string | undefined;
    updated_at: string;
    appointment_date_time_slots: AppointmentDateTimeSlot[];
};
interface AppointmentDateTimeSlot {
    appointment_id: number;
    datetime: string;
}
interface appointmentNotes {
    id: number;
    appointment_id: number;
    appointment_notes: string | undefined;
};

// Interface for the pagination data structure from the API
interface PaginationData {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
}
function PeoplePastAppointmentList() {
    const { getSettingValue } = useGeneralSettings();
    const userDateFormat = getSettingValue('date_format') || 'dd-mm-yyyy';
    const userTimeFormat = getSettingValue('time_format') || '12hr';
    const userTimeZone = getSettingValue('time_zone') || 'UTC';
    
    const [searchInput, setSearchInput] = useState('');
    const [selectedItemsPerPage, setSelectedItemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null);
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [appointmentsList, setAppointmentsList] = useState<Appointment[]>([]);
    const [appointmentsNotesList, setAppointmentsNotesList] = useState<appointmentNotes[]>([]);
    const [newAppointmentNote, setnewAppointmentNote] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [appointmentStatus, setAppointmentStatus] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('');
    // State to hold pagination information from the API
    const [paginationData, setPaginationData] = useState<PaginationData | null>(null);

    const people_id = getPeopleId();
    const navigate = useNavigate();
    const fromDateRef = useRef<Flatpickr>(null);
    const toDateRef = useRef<Flatpickr>(null);

    const { data: appointmentNotesData, refetch: refetchAppointemntNotes } = useQuery(
        ['appointmentNotes', selectedAppointment?.id], // Query key depends on selected appointment
        () => get_all_appointment_notes(selectedAppointment ? selectedAppointment.id : 0),
        {
            enabled: !!selectedAppointment, // Only run query when an appointment is selected
            refetchOnWindowFocus: false
        }
    );


    useEffect(() => {
        if (appointmentNotesData) {
            setAppointmentsNotesList(appointmentNotesData.data as appointmentNotes[]);
        }
    }, [appointmentNotesData]);

    const {
        data: allAppointmentsData,
        isLoading: isAllAppointmentsLoading,
        isError: isAllAppointmentsError,
        refetch
    } = useQuery(
        // The query key is an array of dependencies. When any of these change, React Query refetches.
        ['filteredAppointments', currentPage, selectedItemsPerPage, people_id, fromDate, toDate, appointmentStatus, paymentStatus],
        () => get_filtered_appointments(selectedItemsPerPage, currentPage, people_id, fromDate, toDate, appointmentStatus, paymentStatus, 'past'),
        {
            refetchOnWindowFocus: false,
            onSuccess: (filteredData) => {
                // On successful fetch, update both the appointments list and the pagination data.
                setAppointmentsList(filteredData.data.data);
                setPaginationData(filteredData.data.pagination);
            },
            onError: (error: any) => {
                if (error.response) {
                    const errorMessage = error.response.data.message;
                    toast.error(errorMessage, { position: 'top-right', autoClose: 2000 });
                }
            },
        }
    );

    const handleApplyFilters = () => {
        setCurrentPage(1); // Reset to the first page when applying new filters
        refetch();
    };

    const handleClearFilters = () => {
        setAppointmentStatus('');
        setFromDate('');
        setToDate('');
        setPaymentStatus('');
        setSearchInput('');
        setCurrentPage(1); // Reset to the first page
        // The useQuery will automatically refetch because its dependency 'appointmentStatus', 'fromDate', etc. have changed.
    };

    // Note: This client-side search only filters the data on the *current page*.
    // For a global search across all pages, 'searchInput' should be passed to the API.
    const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
        const searchTerm = e.currentTarget.value;
        setSearchInput(searchTerm);
    };

    // Note: Client-side sorting will only sort the *current page's* data.
    const sortedAppointments = useMemo(() => {
        const filteredList = appointmentsList.filter(appointment => {
            const term = searchInput.toLowerCase();
            return (
                appointment.people_name?.toLowerCase().includes(term) ||
                appointment.full_name?.toLowerCase().includes(term) ||
                appointment.email?.toLowerCase().includes(term)
            );
        });

        if (sortBy) {
            return [...filteredList].sort((a, b) => {
                const aValue = a[sortBy.key] || '';
                const bValue = b[sortBy.key] || '';
                return sortBy.ascending ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
            });
        }
        return filteredList;
    }, [appointmentsList, searchInput, sortBy]);


    const handleStatusChange = useMutation(({ id, status }: { id: number, status: string | undefined }) => {
        const newStatus = status === 'open' ? 'close' : 'open';
        return change_appointment_status(id, newStatus)
            .then((response) => {
                toast.success(response.message, { position: 'top-right', autoClose: 3000 });
                refetch();
            })
            .catch((error: any) => {
                if (error.response) {
                    const errorMessage = error.response.data.message;
                    toast.error(errorMessage, { position: 'top-right', autoClose: 2000 });
                }
            });
    });

    const itemsPerPageOptions = [10, 20, 30, 50, 100];

    const handleDeleteAppointmentNote = useMutation((id: number) => {
        return delete_appointment_note(id)
            .then((response) => {
                toast.success(response.message, { position: 'top-right', autoClose: 3000 });
                refetchAppointemntNotes();
            })
            .catch((error) => {
                if (error.response) {
                    const errorMessage = error.response.data.message;
                    toast.error(errorMessage, { position: 'top-right', autoClose: 2000 });
                }
            });
    });

    const handleNoteChange = (id: number, newNote: string) => {
        setAppointmentsNotesList(prevNotes =>
            prevNotes.map(note =>
                note.id === id ? { ...note, appointment_notes: newNote } : note
            )
        );
    };

    const handleAddAppointmentNotes = useMutation((appointmentNotesData: { appointmentId: number; appointmentNotes: string }) => {
        return add_appointemnt_note(appointmentNotesData.appointmentId, appointmentNotesData.appointmentNotes)
            .then((data) => {
                toast.success(data.message, { position: 'top-right', autoClose: 3000 });
                refetchAppointemntNotes();
                setnewAppointmentNote('');
            })
            .catch((error) => {
                toast.error(error.response.data.message, { position: 'top-right', autoClose: 3000 });
            });
    });

    const handleUpdateAppointmentNotes = useMutation((updatedAppointmentNotesData: { id: number; appointmentId: number; appointmentNotes: string }) => {
        return update_appointemnt_note(updatedAppointmentNotesData.id, updatedAppointmentNotesData.appointmentId, updatedAppointmentNotesData.appointmentNotes)
            .then((data) => {
                toast.success(data.message, { position: 'top-right', autoClose: 3000 });
                refetchAppointemntNotes();
            })
            .catch((error) => {
                toast.error(error.response.data.message, { position: 'top-right', autoClose: 3000 });
            });
    });

    const handleDateFromChange = (date) => {
        setFromDate(date && date[0] ? formatDateFlatpickr(new Date(date[0])) : '');
    };

    const handleDateToChange = (date) => {
        setToDate(date && date[0] ? formatDateFlatpickr(new Date(date[0])) : '');
    };

    const handleSort = (key: string) => {
        setSortBy(prevSortBy => {
            if (prevSortBy && prevSortBy.key === key) {
                return { key, ascending: !prevSortBy.ascending };
            }
            return { key, ascending: true };
        });
    };

    const handleNextPage = () => {
        if (paginationData && currentPage < paginationData.last_page) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleAppointmentLogs = (appointment) => {
        navigate("/appointments/appointment-logs", { state: { id: appointment.id } });
    };

    const handleAppointmentView = (appointment) => {
        navigate("/appointments/view-appointment", { state: { id: appointment.id } });
    };

    return (
        <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
            <div className='card-header border-0 pt-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>List of Past Appointments</span>
                </h3>
            </div>
            <div className='card-header border-0 pt-5 d-flex justify-content-between align-items-center'>
                <h3 className='card-title align-items-start flex-column'>
                    <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                    <input
                        type='text'
                        className='form-control form-control-solid w-250px ps-14'
                        placeholder='Search Appointments'
                        value={searchInput}
                        onChange={handleInputChange}
                    />
                </h3>
                <div className='card-toolbar d-flex align-items-center'>
                    <div className='mx-4'>
                        <select
                            className='form-select form-select-solid form-select-lg fw-bold'
                            value={paymentStatus}
                            onChange={(e) => setPaymentStatus(e.target.value)}
                        >
                            <option value=''>Filter by Payment Status...</option>
                            <option value="pending">Pending</option>
                            <option value="waiting payment">Waiting Payment</option>
                            <option value="Accepted">Accepted</option>
                        </select>
                    </div>
                    <div className='mx-4'>
                        <select
                            className='form-select form-select-solid form-select-lg fw-bold'
                            value={appointmentStatus}
                            onChange={(e) => setAppointmentStatus(e.target.value)}
                        >
                            <option value=''>Filter by Appointment Status...</option>
                            <option value='open'>Open</option>
                            <option value='close'>Close</option>
                            <option value='rejected'>Rejected</option>
                        </select>
                    </div>
                </div>
            </div>
            <div className='card-header border-0 pt-5 mx-3 d-flex flex-column align-items-end'>
                <div className='card-toolbar d-flex align-items-center'>
                    <div className='mx-3'>
                        <Flatpickr
                            ref={fromDateRef}
                            onChange={handleDateFromChange}
                            className='form-control form-control-solid'
                            placeholder='From Date'
                            options={{ dateFormat: 'd/m/Y' }}
                        />
                    </div>
                    <div>
                        <Flatpickr
                            ref={toDateRef}
                            onChange={handleDateToChange}
                            className='form-control form-control-solid'
                            placeholder='To Date'
                            options={{ dateFormat: 'd/m/Y' }}
                        />
                    </div>
                </div>
            </div>
            <div className='card-header border-0 pt-5 mx-3 d-flex flex-column align-items-end'>
                <div>
                    <button className='btn btn-primary me-3' onClick={handleApplyFilters}>Apply Filters</button>
                    <button className='btn btn-light-danger' onClick={handleClearFilters}>Clear Filters</button>
                </div>
            </div>
            <div className='card-body py-3'>
                <div className='table-responsive'>
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        <thead>
                            <tr className='fw-bold text-muted'>
                                <th className='min-w-200px'>Appointment With <span onClick={() => handleSort('people_name')}><KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                <th className='min-w-200px'>Appointment By <span onClick={() => handleSort('full_name')}><KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                <th className='min-w-180px'>Phone <span onClick={() => handleSort('phone_number')}><KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                <th className='min-w-180px'>Subcategory <span onClick={() => handleSort('subcategory_title')}><KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                <th className='min-w-140px'>Customer Email <span onClick={() => handleSort('email')}><KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                <th className='min-w-200px'>Date/Time <span onClick={() => handleSort('appointment_date_time')}><KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                <th className='min-w-150px'>Status <span onClick={() => handleSort('appointment_status')}><KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                <th className='min-w-150px'>Updated At <span onClick={() => handleSort('updated_at')}><KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                <th className='min-w-150px'>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isAllAppointmentsError ? (
                                <tr><td colSpan={9}>Error loading data. Please try again later.</td></tr>
                            ) : isAllAppointmentsLoading ? (
                                <tr><td colSpan={9} className='text-center'><div className='spinner-border' role='status'><span className='visually-hidden'>Loading...</span></div></td></tr>
                            ) : sortedAppointments.length > 0 ? (
                                sortedAppointments.map((appointment) => (
                                    <tr key={appointment.id}>
                                        <td>{appointment.people_name}</td>
                                        <td>{appointment.full_name}</td>
                                        <td>{appointment.phone_number}</td>
                                        <td>{appointment.subcategory_title}</td>
                                        <td>{appointment.email}</td>
                                        <td>
                                            {appointment.appointment_date_time_slots?.[0]?.datetime
                                                ? formatDynamicDateTime(
                                                    appointment.appointment_date_time_slots[0].datetime,
                                                    userDateFormat,
                                                    userTimeFormat,
                                                    userTimeZone
                                                )
                                                : "-"}
                                        </td>
                                        <td>
                                            <span className={`badge ${appointment.appointment_status === 'open' ? 'badge-light-success' : 'badge-light-warning'}`}>
                                                {appointment.appointment_status}
                                            </span>
                                        </td>
                                        <td>{formatDynamicDateTime(appointment.updated_at, userDateFormat, userTimeFormat, userTimeZone)}</td>
                                        <td>
                                            <div className='d-flex justify-content-end flex-shrink-0'>
                                                {appointment.appointment_status === 'open' && (
                                                    <button className="btn btn-sm btn-success text-sm py-0 px-2 mx-1 fs-9" onClick={() => handleStatusChange.mutate({ id: appointment.id, status: appointment.appointment_status })}>
                                                        Change Status
                                                    </button>
                                                )}
                                                <button className='btn btn-icon btn-light-dark btn-active-color-primary btn-sm me-1' data-bs-toggle='modal' data-bs-target='#kt_modal_2' onClick={() => setSelectedAppointment(appointment)}>
                                                    <KTIcon iconName='file' className='fs-3' />
                                                </button>
                                                <button onClick={() => handleAppointmentView(appointment)} className='btn btn-icon btn-light-dark btn-active-color-warning btn-sm me-1'>
                                                    <KTIcon iconName='eye' className='fs-3' />
                                                </button>
                                                <button onClick={() => handleAppointmentLogs(appointment)} className='btn btn-icon btn-light-dark btn-active-color-warning btn-sm me-1'>
                                                    <i className='bi bi-journal-text'></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={9}>No Appointments Available.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal for Appointment Notes */}
            <div className='modal fade' tabIndex={-1} id='kt_modal_2'>
                {/* ... Modal content is unchanged ... */}
            </div>

            {/* Corrected and De-duplicated Pagination Footer */}
            <div className='card-footer d-flex justify-content-between align-items-center py-4'>
                <div className='d-flex align-items-center'>
                    <strong className='me-3'>Results Per Page:</strong>
                    <select
                        className='form-select form-select-sm form-select-solid'
                        style={{ width: '80px' }}
                        value={selectedItemsPerPage}
                        onChange={(e) => {
                            setSelectedItemsPerPage(Number(e.target.value));
                            setCurrentPage(1); // Reset to page 1 when changing items per page
                        }}
                    >
                        {itemsPerPageOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                <div className='d-flex align-items-center'>
                    {paginationData && (
                        <span className='text-muted me-3'>
                            Page <strong>{paginationData.current_page}</strong> of <strong>{paginationData.last_page}</strong>
                        </span>
                    )}
                    <div className='d-flex'>
                        <button
                            className='btn btn-light-primary btn-sm'
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <button
                            className='btn btn-primary btn-sm ms-2'
                            onClick={handleNextPage}
                            disabled={!paginationData || currentPage === paginationData.last_page}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PeoplePastAppointmentList;